import type { Frame } from './types';

export interface Pos { x: number; y: number }
export type AlignEdge = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeAxis = 'h' | 'v';

const DEFAULT_GUTTER = 32;
function fh(f: Frame): number { return f.h ?? 200; }

/**
 * Pack frames into a clean grid, preserving rough reading order (top-to-bottom,
 * left-to-right). Columns ≈ √n; column pitch = widest frame + gutter; each row's
 * height = tallest frame in that row. Anchored at the set's current top-left so
 * the cluster stays roughly where it was. Returns target positions by id.
 */
export function gridTidy(frames: Frame[], gutter = DEFAULT_GUTTER): Map<string, Pos> {
  const out = new Map<string, Pos>();
  if (frames.length === 0) return out;
  const anchorX = Math.min(...frames.map((f) => f.x));
  const anchorY = Math.min(...frames.map((f) => f.y));
  const pitchX = Math.max(...frames.map((f) => f.w)) + gutter;
  const cols = Math.max(1, Math.ceil(Math.sqrt(frames.length)));

  const ordered = [...frames].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  let rowY = anchorY;
  let rowMaxH = 0;
  ordered.forEach((f, i) => {
    const col = i % cols;
    if (col === 0 && i > 0) {
      rowY += rowMaxH + gutter;
      rowMaxH = 0;
    }
    out.set(f.id, { x: anchorX + col * pitchX, y: rowY });
    rowMaxH = Math.max(rowMaxH, fh(f));
  });
  return out;
}

/** Align frames to a shared edge/center within their bounding box. */
export function alignFrames(frames: Frame[], edge: AlignEdge): Map<string, Pos> {
  const out = new Map<string, Pos>();
  if (frames.length < 2) return out;
  const minX = Math.min(...frames.map((f) => f.x));
  const minY = Math.min(...frames.map((f) => f.y));
  const maxX = Math.max(...frames.map((f) => f.x + f.w));
  const maxY = Math.max(...frames.map((f) => f.y + fh(f)));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  for (const f of frames) {
    let { x, y } = f;
    switch (edge) {
      case 'left': x = minX; break;
      case 'right': x = maxX - f.w; break;
      case 'hcenter': x = cx - f.w / 2; break;
      case 'top': y = minY; break;
      case 'bottom': y = maxY - fh(f); break;
      case 'vcenter': y = cy - fh(f) / 2; break;
    }
    out.set(f.id, { x, y });
  }
  return out;
}

/**
 * Distribute frames so the gaps between them are equal along an axis. First and
 * last (by position) stay put; the rest are spread between them. Needs ≥3.
 */
export function distributeFrames(frames: Frame[], axis: DistributeAxis): Map<string, Pos> {
  const out = new Map<string, Pos>();
  if (frames.length < 3) return out;
  const size = (f: Frame) => (axis === 'h' ? f.w : fh(f));
  const coord = (f: Frame) => (axis === 'h' ? f.x : f.y);
  const ordered = [...frames].sort((a, b) => coord(a) - coord(b));
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  const span = (coord(last) + size(last)) - coord(first);
  const totalSize = ordered.reduce((s, f) => s + size(f), 0);
  const gap = (span - totalSize) / (ordered.length - 1);
  let cursor = coord(first);
  for (const f of ordered) {
    out.set(f.id, axis === 'h' ? { x: cursor, y: f.y } : { x: f.x, y: cursor });
    cursor += size(f) + gap;
  }
  return out;
}
